/**
 * Atalhos de teclado no desktop. As teclas viram eventos da janela
 * ("ydchat:<nome>") e cada tela escuta só o que lhe interessa — assim a
 * lista, a conversa e o compositor não precisam se conhecer.
 * Com Alt, funcionam mesmo digitando (e.code: no Mac, Alt muda e.key).
 */
import { useEffect, useRef } from "react";

export const ATALHOS: { teclas: string[]; acao: string }[] = [
  { teclas: ["Ctrl", "K"], acao: "Busca geral (clientes e mensagens)" },
  { teclas: ["/"], acao: "Buscar na lista de conversas" },
  { teclas: ["Alt", "↓"], acao: "Próxima conversa da lista" },
  { teclas: ["Alt", "↑"], acao: "Conversa anterior" },
  { teclas: ["Alt", "N"], acao: "Cliente que espera há mais tempo" },
  { teclas: ["Alt", "S"], acao: "Pedir sugestão de resposta à IA" },
  { teclas: ["Alt", "V"], acao: "Abrir a venda da conversa" },
  { teclas: ["Alt", "I"], acao: "Mostrar ou esconder o painel do cliente" },
  { teclas: ["Enter"], acao: "Enviar mensagem" },
  { teclas: ["Shift", "Enter"], acao: "Nova linha na mensagem" },
  { teclas: ["/"], acao: "No campo de mensagem: respostas rápidas" },
  { teclas: ["Esc"], acao: "Fechar painel ou cancelar resposta citada" },
  { teclas: ["?"], acao: "Ver esta lista" },
];

export type EventoAtalho =
  | "proxima-conversa"
  | "conversa-anterior"
  | "proxima-esperando"
  | "sugerir"
  | "abrir-venda"
  | "alternar-contexto"
  | "ajuda";

export const emitir = (e: EventoAtalho) => window.dispatchEvent(new Event(`ydchat:${e}`));

/** Escuta um atalho; a função mais recente é sempre a usada. */
export function useAtalho(e: EventoAtalho, fn: () => void) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const h = () => ref.current();
    window.addEventListener(`ydchat:${e}`, h);
    return () => window.removeEventListener(`ydchat:${e}`, h);
  }, [e]);
}

const COM_ALT: Record<string, EventoAtalho> = {
  ArrowDown: "proxima-conversa",
  ArrowUp: "conversa-anterior",
  KeyN: "proxima-esperando",
  KeyS: "sugerir",
  KeyV: "abrir-venda",
  KeyI: "alternar-contexto",
};

/** Liga os atalhos globais (uma vez, no AppShell). */
export function useAtalhosGlobais() {
  useEffect(() => {
    const tecla = (e: KeyboardEvent) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey) {
        const ev = COM_ALT[e.code] ?? COM_ALT[e.key];
        if (ev) {
          e.preventDefault();
          emitir(ev);
        }
        return;
      }
      const alvo = e.target as HTMLElement;
      const digitando =
        ["INPUT", "TEXTAREA", "SELECT"].includes(alvo.tagName) || alvo.isContentEditable;
      if (!digitando && e.key === "?") {
        e.preventDefault();
        emitir("ajuda");
      }
    };
    window.addEventListener("keydown", tecla);
    return () => window.removeEventListener("keydown", tecla);
  }, []);
}
